import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ServerIcon } from '@heroicons/react/24/outline';
import { HAProxyInstance } from '../../types';

// Create a type for creating a new instance (without id)
type NewHAProxyInstance = Pick<HAProxyInstance, 'name' | 'host' | 'port' | 'apiKey'>;

// Create a type for updating an existing instance
type UpdateHAProxyInstance = NewHAProxyInstance & { id: string };

interface InstanceFormProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (instance: NewHAProxyInstance | UpdateHAProxyInstance) => void;
  instance?: HAProxyInstance;
}

const InstanceForm: React.FC<InstanceFormProps> = ({
  isOpen,
  onCancel,
  onSave,
  instance
}) => {
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('8404');
  const [apiKey, setApiKey] = useState('');

  // Reset form when opened or when editing a different instance
  useEffect(() => {
    if (isOpen) {
      if (instance) {
        setName(instance.name);
        setHost(instance.host);
        setPort(instance.port.toString());
        setApiKey(instance.apiKey || '');
      } else {
        setName('');
        setHost('');
        setPort('8404');
        setApiKey('');
      }
    }
  }, [isOpen, instance]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const instanceData: NewHAProxyInstance = {
      name,
      host,
      port: parseInt(port, 10),
      apiKey: apiKey || undefined
    };

    if (instance) {
      // If editing, include the ID
      onSave({ 
        ...instanceData, 
        id: instance.id 
      });
    } else {
      // If adding new, don't include ID
      onSave(instanceData);
    }
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-10 overflow-y-auto"
        onClose={onCancel}
      >
        <div className="min-h-screen px-4 text-center">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black opacity-50" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-zinc-900 shadow-xl rounded-lg">
              <Dialog.Title
                as="h3"
                className="text-lg font-medium leading-6 text-zinc-100 flex items-center"
              >
                <ServerIcon className="h-5 w-5 mr-2 text-indigo-400" />
                {instance ? 'Edit HAProxy Instance' : 'Add HAProxy Instance'}
              </Dialog.Title>
              
              <form onSubmit={handleSubmit} className="mt-4">
                <div className="mb-4">
                  <label htmlFor="name" className="block text-sm font-medium text-zinc-400 mb-1">
                    Instance Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="host" className="block text-sm font-medium text-zinc-400 mb-1">
                    Host
                  </label>
                  <input
                    type="text"
                    id="host"
                    required
                    value={host}
                    onChange={(e) => setHost(e.target.value)}
                    className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="port" className="block text-sm font-medium text-zinc-400 mb-1">
                    Port
                  </label>
                  <input
                    type="number"
                    id="port"
                    required
                    min="1"
                    max="65535"
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                    className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="apiKey" className="block text-sm font-medium text-zinc-400 mb-1">
                    API Token
                  </label>
                  <input
                    type="text"
                    id="apiKey"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 rounded-md text-sm font-medium text-zinc-300 bg-zinc-700 hover:bg-zinc-600 focus:outline-none"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none"
                  >
                    {instance ? 'Update' : 'Add'} Instance
                  </button>
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default InstanceForm; 